// src/components/admin/AdminDemandes.jsx
// Demandes des organisateurs (report / annulation / modification) avec billets vendus.

import React, { useState } from "react";
import { getDemandes, traiterDemande, reporterEvenement, annulerEvenementAdmin } from "../../services/AdminGestionService";
import { useListeAdmin, TableAdmin, Pagination, Filtre, EnTete, Badge, Action, BoutonActualiser, dateFr } from "./AdminUI";

const TYPES = {
    report: ["bleu", "Report"],
    annulation: ["rouge", "Annulation"],
    modification: ["violet", "Modification"],
};
const STATUTS = {
    en_attente: ["orange", "À traiter"],
    traitee: ["vert", "Traitée"],
    refusee: ["gris", "Refusée"],
};

const AdminDemandes = () => {
    const L = useListeAdmin(getDemandes, { statut: "en_attente" });
    const [message, setMessage] = useState(null);

    const agir = async (fn, confirmation) => {
        if (confirmation && !window.confirm(confirmation)) return;
        try {
            const r = await fn();
            setMessage({ ok: r.success !== false, texte: r.message || "Fait." });
            L.recharger();
        } catch (e) {
            setMessage({ ok: false, texte: e.message });
        }
    };

    // Valider = faire l'action demandée, puis marquer la demande traitée
    const valider = (d) => {
        if (d.type === "annulation") {
            agir(async () => {
                const r = await annulerEvenementAdmin(d.evenement_id, d.message);
                await traiterDemande(d.id, "traitee");
                return r;
            }, `Annuler « ${d.nomEven} » ?\n${d.nb_reservations} réservation(s) seront remboursées (hors frais de paiement) et prévenues par e-mail.`);
        } else if (d.type === "report") {
            const date = window.prompt("Nouvelle date (AAAA-MM-JJ HH:MM) :", d.nouvelle_date ? String(d.nouvelle_date).slice(0, 16) : "");
            if (!date) return;
            agir(async () => {
                const r = await reporterEvenement(d.evenement_id, date);
                await traiterDemande(d.id, "traitee");
                return r;
            });
        } else {
            agir(() => traiterDemande(d.id, "traitee"), "Marquer comme traitée ? (fais la modification à la main avant)");
        }
    };

    const colonnes = [
        { cle: "created_at", titre: "Reçue le", rendu: (d) => <span className="text-xs text-gray-500 whitespace-nowrap">{dateFr(d.created_at, true)}</span> },
        { cle: "type", titre: "Type", rendu: (d) => { const t = TYPES[d.type] || ["gris", d.type]; return <Badge couleur={t[0]}>{t[1]}</Badge>; } },
        { cle: "nomEven", titre: "Événement", rendu: (d) => (
            <div className="max-w-[200px]">
                <div className="font-semibold text-gray-900 line-clamp-1">{d.nomEven}</div>
                <div className="text-[11px] text-gray-500">le {dateFr(d.dateEven, true)}</div>
                {d.nouvelle_date && <div className="text-[11px] text-blue-700">→ souhaité : {dateFr(d.nouvelle_date, true)}</div>}
            </div>
        )},
        { cle: "email", titre: "Organisateur", rendu: (d) => (
            <div>
                <div className="text-gray-800">{d.name}</div>
                <a href={`mailto:${d.email}`} className="text-[11px] text-violet-700 hover:underline">{d.email}</a>
            </div>
        )},
        { cle: "nb_reservations", titre: "Résa.", classe: "text-center" },
        { cle: "message", titre: "Motif", rendu: (d) => <p className="text-xs text-gray-700 max-w-[280px] whitespace-pre-line">{d.message}</p> },
        { cle: "statut", titre: "Statut", rendu: (d) => { const s = STATUTS[d.statut] || ["gris", d.statut]; return <Badge couleur={s[0]}>{s[1]}</Badge>; } },
        { cle: "actions", titre: "", rendu: (d) => d.statut === "en_attente" ? (
            <div className="flex flex-col items-start gap-0.5">
                <Action couleur="vert" onClick={() => valider(d)}>
                    {d.type === "annulation" ? "Annuler l'événement" : d.type === "report" ? "Reporter" : "Traitée"}
                </Action>
                <Action couleur="rouge" onClick={() => agir(() => traiterDemande(d.id, "refusee"), "Refuser cette demande ? Pense à prévenir l'organisateur.")}>
                    Refuser
                </Action>
            </div>
        ) : null },
    ];

    return (
        <div className="max-w-7xl mx-auto">
            <EnTete titre="Demandes des organisateurs"
                sousTitre="Report, annulation ou modification d'un événement qui a déjà des billets vendus"
                actions={<>
                    <Filtre valeur={L.filtres.statut} onChange={(v) => L.setFiltre("statut", v)}
                        options={[["en_attente", "À traiter"], ["traitee", "Traitées"], ["refusee", "Refusées"], ["", "Toutes"]]} />
                    <BoutonActualiser onClick={L.recharger} />
                </>}
            />
            {message && (
                <div className={`mb-4 px-4 py-2.5 rounded-xl text-sm border ${message.ok ? "bg-green-50 text-green-700 border-green-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                    {message.texte}
                </div>
            )}
            <TableAdmin colonnes={colonnes} lignes={L.page?.data} loading={L.loading} erreur={L.erreur} vide="Aucune demande." />
            <Pagination page={L.page} onPage={L.allerA} onParPage={L.setParPage} />
        </div>
    );
};

export default AdminDemandes;